import * as React from "react"
import { format, isBefore, startOfDay, setHours, setMinutes } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface DateTimePickerProps {
  /** Selected date/time as Date object */
  value?: Date
  /** Callback when date/time changes */
  onChange: (date: Date | undefined) => void
  /** Minimum allowed date (defaults to now) */
  minDate?: Date
  /** Optional className for the trigger button */
  className?: string
  /** Placeholder text when no date is selected */
  placeholder?: string
}

/**
 * DateTimePicker component that combines a date calendar with time selection.
 * Returns a Date object that can be converted to Unix timestamp for API calls.
 */
export function DateTimePicker({
  value,
  onChange,
  minDate,
  className,
  placeholder = "Pick a date and time",
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)

  // Minimum date defaults to now (no past dates)
  const effectiveMinDate = minDate || new Date()

  // Get current hour and minute for defaults
  const now = new Date()
  const currentHour = now.getHours()
  const currentMinute = now.getMinutes()

  // Extract hour and minute from value, default to next hour rounded to nearest 15 min
  const selectedHour = value ? value.getHours() : Math.min(currentHour + 1, 23)
  const selectedMinute = value ? value.getMinutes() : Math.ceil(currentMinute / 15) * 15 % 60

  // Generate hour options (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i)

  // Generate minute options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45]

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onChange(undefined)
      return
    }

    // Preserve the time if already set, otherwise use defaults
    const hour = value ? value.getHours() : selectedHour
    const minute = value ? value.getMinutes() : selectedMinute

    let newDateTime = setMinutes(setHours(date, hour), minute)

    // If the selected date/time is in the past, bump to the next valid time
    if (isBefore(newDateTime, effectiveMinDate)) {
      // If it's today, set to next available time slot
      if (startOfDay(date).getTime() === startOfDay(effectiveMinDate).getTime()) {
        const nextHour = Math.min(effectiveMinDate.getHours() + 1, 23)
        const nextMinute = Math.ceil(effectiveMinDate.getMinutes() / 15) * 15 % 60
        newDateTime = setMinutes(setHours(date, nextHour), nextMinute)
      }
    }

    onChange(newDateTime)
  }

  const handleHourChange = (hourStr: string) => {
    const hour = parseInt(hourStr, 10)
    const baseDate = value || new Date()
    const newDateTime = setHours(baseDate, hour)
    onChange(newDateTime)
  }

  const handleMinuteChange = (minuteStr: string) => {
    const minute = parseInt(minuteStr, 10)
    const baseDate = value || new Date()
    const newDateTime = setMinutes(baseDate, minute)
    onChange(newDateTime)
  }

  // Check if a time option is disabled (in the past)
  const isTimeDisabled = (hour: number, minute: number): boolean => {
    if (!value) return false

    const testDateTime = setMinutes(setHours(value, hour), minute)
    return isBefore(testDateTime, effectiveMinDate)
  }

  // Format display string
  const displayValue = value
    ? format(value, "MMM d, yyyy 'at' h:mm a")
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleDateSelect}
            disabled={(date) => isBefore(startOfDay(date), startOfDay(effectiveMinDate))}
            initialFocus
          />

          <div className="border-t mt-3 pt-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Time:</span>
              <Select
                value={selectedHour.toString()}
                onValueChange={handleHourChange}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder="Hour" />
                </SelectTrigger>
                <SelectContent>
                  {hours.map((hour) => (
                    <SelectItem
                      key={hour}
                      value={hour.toString()}
                      disabled={isTimeDisabled(hour, selectedMinute)}
                    >
                      {hour.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-muted-foreground">:</span>
              <Select
                value={selectedMinute.toString()}
                onValueChange={handleMinuteChange}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder="Min" />
                </SelectTrigger>
                <SelectContent>
                  {minutes.map((minute) => (
                    <SelectItem
                      key={minute}
                      value={minute.toString()}
                      disabled={isTimeDisabled(selectedHour, minute)}
                    >
                      {minute.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground ml-2">
                ({format(value || setMinutes(setHours(new Date(), selectedHour), selectedMinute), 'h:mm a')})
              </span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Helper to convert Date to Unix timestamp (seconds)
 */
export function dateToUnixTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000)
}
